/**
 * Ajuste de preço de um pedido (#274) — por prato ou no total.
 *
 * Garçom: PEDE o ajuste (motivo obrigatório); só vale depois que um Gerente
 * ou o Dono aprova (#284). Gerente/Dono: aplicam direto.
 */
import { useMemo, useState } from 'react'
import { adjustmentsApi } from '../lib/api.js'
import type { AdjustmentInput, AdjustmentType, OrderDetailed } from '../lib/api.js'
import { useI18n } from '../lib/i18n.js'
import { Button, Input, Modal, Select, Textarea, toast } from './ui/index.js'

type Props = {
  order: OrderDetailed | null
  /** true = Gerente/Dono (aplica direto); false = garçom (pede aprovação) */
  canApprove: boolean
  onClose: () => void
  onSaved: () => void
  formatMoney: (cents: number) => string
}

export function PriceAdjustmentModal({ order, canApprove, onClose, onSaved, formatMoney }: Props) {
  const { t } = useI18n()
  const [target, setTarget] = useState<string>('order')
  const [type, setType] = useState<AdjustmentType>('discount_percent')
  const [value, setValue] = useState('')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  const isItem = target !== 'order'
  const typeOptions = useMemo(
    () =>
      [
        { value: 'discount_percent', label: t('admin.adjust.type_discount_percent') },
        { value: 'discount_amount', label: t('admin.adjust.type_discount_amount') },
        ...(isItem
          ? [{ value: 'price_override', label: t('admin.adjust.type_price_override') }]
          : []),
        { value: 'none', label: t('admin.adjust.type_none') },
      ] as Array<{ value: string; label: string }>,
    [isItem, t],
  )

  if (!order) return null

  function reset() {
    setTarget('order')
    setType('discount_percent')
    setValue('')
    setReason('')
  }

  async function submit() {
    if (!order) return
    const effectiveType: AdjustmentType =
      !isItem && type === 'price_override' ? 'discount_percent' : type
    let numeric: number | undefined
    if (effectiveType !== 'none') {
      const n = Number(value.replace(',', '.'))
      if (!Number.isFinite(n) || n <= 0) {
        toast.error(t('admin.adjust.value_required'))
        return
      }
      numeric = effectiveType === 'discount_percent' ? Math.round(n) : Math.round(n * 100)
    }
    if (!canApprove && !reason.trim()) {
      toast.error(t('admin.adjust.reason_required'))
      return
    }
    const body = (
      isItem
        ? { target: 'item', order_item_id: target, type: effectiveType, value: numeric }
        : { target: 'order', type: effectiveType, value: numeric }
    ) as AdjustmentInput
    if (reason.trim()) body.reason = reason.trim()
    setSaving(true)
    try {
      if (canApprove) {
        await adjustmentsApi.apply(order.id, body)
        toast.success(t('admin.adjust.applied_toast'))
      } else {
        const req = await adjustmentsApi.request(order.id, body)
        // dentro do limite do garçom → aplicado na hora (0056)
        toast.success(
          req.auto_approved
            ? t('admin.adjust.auto_applied_toast')
            : t('admin.adjust.requested_toast'),
        )
      }
      reset()
      onSaved()
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('admin.common.error'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={!!order}
      onClose={onClose}
      title={canApprove ? t('admin.adjust.title_apply') : t('admin.adjust.title_request')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('admin.common.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={saving} data-testid="adjust-submit">
            {canApprove ? t('admin.adjust.apply') : t('admin.adjust.request')}
          </Button>
        </>
      }
    >
      <div className="space-y-3" data-testid="adjust-modal">
        {!canApprove && (
          <p className="text-xs rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2">
            {t('admin.adjust.needs_approval')}
          </p>
        )}
        <Select
          label={t('admin.adjust.target')}
          data-testid="adjust-target"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          options={[
            {
              value: 'order',
              label: `${t('admin.adjust.target_order')} (${formatMoney(order.total_cents)})`,
            },
            ...order.items.map((i) => ({
              value: i.id,
              label: `${i.quantity}× ${i.product_name} (${formatMoney(i.unit_price_cents)})`,
            })),
          ]}
        />
        <Select
          label={t('admin.adjust.type')}
          data-testid="adjust-type"
          value={type}
          onChange={(e) => setType(e.target.value as AdjustmentType)}
          options={typeOptions}
        />
        {type !== 'none' && (
          <Input
            label={
              type === 'discount_percent'
                ? t('admin.adjust.value_percent')
                : t('admin.adjust.value_money')
            }
            data-testid="adjust-value"
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
        <Textarea
          label={canApprove ? t('admin.adjust.reason_optional') : t('admin.adjust.reason')}
          data-testid="adjust-reason"
          value={reason}
          maxLength={300}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
    </Modal>
  )
}
